import {
  AuthError,
  AuthErrorCategory,
  AuthErrorSeverity,
  AuthErrorDetails,
  AuthNetworkError,
  AuthValidationError,
  AuthenticationError,
  AuthSystemError
} from '../../types/auth/AuthError';

class ErrorReportingService {
  private static instance: ErrorReportingService;
  private sessionId: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  public static getInstance(): ErrorReportingService {
    if (!ErrorReportingService.instance) {
      ErrorReportingService.instance = new ErrorReportingService();
    }
    return ErrorReportingService.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public reportNetworkError(params: {
    message: string;
    operation: string;
    code?: string;
    statusCode?: number;
    endpoint?: string;
    retryable?: boolean;
    context?: Record<string, any>;
    userId?: string;
  }): AuthNetworkError {
    const error: AuthNetworkError = {
      category: AuthErrorCategory.NETWORK,
      severity: params.statusCode && params.statusCode >= 500 ? AuthErrorSeverity.HIGH : AuthErrorSeverity.MEDIUM,
      message: params.message,
      code: params.code,
      operation: params.operation,
      statusCode: params.statusCode,
      endpoint: params.endpoint,
      retryable: params.retryable ?? true,
      context: params.context,
      timestamp: new Date(),
      userId: params.userId,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent
    };

    this.logError(error);
    return error;
  }

  public reportValidationError(params: {
    message: string;
    operation: string;
    code?: string;
    field?: string;
    expectedFormat?: string;
    context?: Record<string, any>;
    userId?: string;
  }): AuthValidationError {
    const error: AuthValidationError = {
      category: AuthErrorCategory.VALIDATION,
      severity: AuthErrorSeverity.LOW,
      message: params.message,
      code: params.code,
      operation: params.operation,
      field: params.field,
      expectedFormat: params.expectedFormat,
      context: params.context,
      timestamp: new Date(),
      userId: params.userId,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent
    };

    this.logError(error);
    return error;
  }

  public reportAuthError(params: {
    message: string;
    operation: string;
    code?: string;
    challengeType?: string;
    requiresReauth?: boolean;
    context?: Record<string, any>;
    userId?: string;
  }): AuthenticationError {
    const error: AuthenticationError = {
      category: AuthErrorCategory.AUTH,
      severity: params.requiresReauth ? AuthErrorSeverity.HIGH : AuthErrorSeverity.MEDIUM,
      message: params.message,
      code: params.code,
      operation: params.operation,
      challengeType: params.challengeType,
      requiresReauth: params.requiresReauth,
      context: params.context,
      timestamp: new Date(),
      userId: params.userId,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent
    };

    this.logError(error);
    return error;
  }

  public reportSystemError(params: {
    message: string;
    operation: string;
    code?: string;
    error?: Error;
    componentName?: string;
    context?: Record<string, any>;
    userId?: string;
  }): AuthSystemError {
    const error: AuthSystemError = {
      category: AuthErrorCategory.SYSTEM,
      severity: AuthErrorSeverity.CRITICAL,
      message: params.message,
      code: params.code,
      operation: params.operation,
      stackTrace: params.error?.stack,
      componentName: params.componentName,
      context: params.context,
      timestamp: new Date(),
      userId: params.userId,
      sessionId: this.sessionId,
      userAgent: navigator.userAgent
    };

    this.logError(error);
    return error;
  }

  private logError(error: AuthError): void {
    // Structured logging with categorisation
    const logData = {
      timestamp: error.timestamp.toISOString(),
      level: 'ERROR',
      category: error.category,
      severity: error.severity,
      operation: error.operation,
      message: error.message,
      code: error.code,
      sessionId: error.sessionId,
      userId: error.userId,
      userAgent: error.userAgent,
      context: error.context
    };

    // Add specific fields based on error type
    if (error.category === AuthErrorCategory.NETWORK) {
      const networkError = error as AuthNetworkError;
      Object.assign(logData, {
        statusCode: networkError.statusCode,
        endpoint: networkError.endpoint,
        retryable: networkError.retryable
      });
    } else if (error.category === AuthErrorCategory.VALIDATION) {
      const validationError = error as AuthValidationError;
      Object.assign(logData, {
        field: validationError.field,
        expectedFormat: validationError.expectedFormat
      });
    } else if (error.category === AuthErrorCategory.AUTH) {
      const authError = error as AuthenticationError;
      Object.assign(logData, {
        challengeType: authError.challengeType,
        requiresReauth: authError.requiresReauth
      });
    } else if (error.category === AuthErrorCategory.SYSTEM) {
      const systemError = error as AuthSystemError;
      Object.assign(logData, {
        componentName: systemError.componentName,
        stackTrace: systemError.stackTrace
      });
    }

    // Log to console with structured format (replacing console.log)
    console.error('[AUTH_ERROR]', JSON.stringify(logData, null, 2));

    // In production, you might want to send this to a logging service
    // this.sendToLoggingService(logData);
  }

  public reportInfo(operation: string, message: string, context?: Record<string, any>): void {
    const logData = {
      timestamp: new Date().toISOString(),
      level: 'INFO',
      operation,
      message,
      sessionId: this.sessionId,
      context
    };

    console.info('[AUTH_INFO]', JSON.stringify(logData, null, 2));
  }

  public reportDebug(operation: string, message: string, data?: any): void {
    const logData = {
      timestamp: new Date().toISOString(),
      level: 'DEBUG',
      operation,
      message,
      sessionId: this.sessionId,
      data
    };

    // Only log debug in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('[AUTH_DEBUG]', JSON.stringify(logData, null, 2));
    }
  }
}

export const errorReportingService = ErrorReportingService.getInstance();